import { useState, useCallback, useRef } from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { get, paramsToUrl } from '@/utils'

/**
 * Factory hook for creating inline remote validation with debouncing
 * @returns {Object} Factory that can create validators
 */
export const useRemoteValidationFactory = () => {
  // Store validator states in a map
  const [validatorsState, setValidatorsState] = useState({});

  // Cache for validators to avoid recreating them
  const validatorsCache = useRef({});
  
  // Store timeout IDs for debouncing
  const timeoutIds = useRef({});

  /**
   * Creates a validator for a specific field with internal caching
   * @param {Object} config Validator configuration
   * @param {string} config.id Unique ID for this validator
   * @param {string} config.endpoint API endpoint for validation
   * @param {Function} config.processResponse Function to process response (returns error message or undefined)
   * @param {Function} config.clearErrors Optional function to clear form errors
   * @param {number} config.debounceTime Debounce time in ms (default: 300ms)
   * @returns {Object} Validator with validation function, props, and indicator
   */
  const createValidator = useCallback((config) => {
    const { 
      id, 
      endpoint, 
      processResponse, 
      clearErrors,
      debounceTime = 500 // Default debounce time 
    } = config;

    // Function to get merged props - defined before it's used
    const getFieldProps = (field, validatorId) => {
      // Start with all original field props
      const mergedProps = { ...field };
      
      // Then add custom onChange that chains both handlers
      const originalOnChange = field.onChange;
      
      mergedProps.onChange = (e) => {
        // First call React Hook Form's onChange to update the form
        originalOnChange(e);
        
        // Clear form errors if clearErrors function was provided
        if (clearErrors) {
          clearErrors(validatorId);  // Use the validator ID as the field name
        }
        
        // Then handle clearing validation status
        const currentState = validatorsState[validatorId];
        if (currentState?.status) {
          setValidatorsState(prev => ({
            ...prev,
            [validatorId]: { ...prev[validatorId], status: null }
          }));
        }
      };
      
      return mergedProps;
    };

    // Create a cache key based on the config
    const cacheKey = `${id}-${endpoint}`;

    // Return cached validator if it exists
    if (validatorsCache.current[cacheKey]) {
      // Update the indicator based on current state
      const cachedValidator = validatorsCache.current[cacheKey];
      const currentState = validatorsState[id] || { isValidating: false, status: null };

      // Update the indicator element based on current state
      const indicator = (() => {
        if (currentState.isValidating) {
          return (
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          );
        }

        if (currentState.status === 'success') {
          return <CheckCircle size={20} className="text-green-600" />;
        }

        if (currentState.status === 'error') {
          return <AlertCircle size={20} className="text-destructive" />;
        }

        return null;
      })();

      // Return updated validator with fresh indicator and merged props function
      return {
        ...cachedValidator,
        getMergedProps: (field) => getFieldProps(field, id),
        indicator,
        state: currentState
      };
    }

    // Create a function that actually performs the validation
    const performValidation = async (value) => {
      if (!value) return true;

      try {
        let parameters = { value };
        const url = paramsToUrl(endpoint, parameters);
        const result = await get(url, {}, { showLoading: false });

        let errorMessage;

        if (processResponse) {
          errorMessage = processResponse(result);
        } else {
          errorMessage = result.ErrorMessage;
        }

        // Update state based on validation result
        setValidatorsState(prev => ({
          ...prev,
          [id]: {
            isValidating: false,
            status: errorMessage ? 'error' : 'success'
          }
        }));

        return errorMessage || true;
      } catch (error) {
        // Update state for error case
        setValidatorsState(prev => ({
          ...prev,
          [id]: { isValidating: false, status: 'error' }
        }));

        return 'Failed to validate. Please try again.';
      }
    };

    // Create debounced validation function
    const validateFn = async (value) => {
      // Clear any existing timeout for this validator
      if (timeoutIds.current[id]) {
        clearTimeout(timeoutIds.current[id]);
      }

      // If no value, return immediately without setting up debounce
      if (!value) return true;

      // Update state to show loading
      setValidatorsState(prev => ({
        ...prev,
        [id]: { isValidating: true, status: null }
      }));

      // Return a promise that resolves when the debounced validation completes
      return new Promise((resolve) => {
        // Set up new timeout
        timeoutIds.current[id] = setTimeout(async () => {
          const result = await performValidation(value);
          resolve(result);
          // Clean up the timeout ID
          delete timeoutIds.current[id];
        }, debounceTime);
      });
    };

    // Get current state for this validator
    const currentState = validatorsState[id] || { isValidating: false, status: null };

    // Create indicator element based on current state
    const indicator = (() => {
      if (currentState.isValidating) {
        return (
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        );
      }

      if (currentState.status === 'success') {
        return <CheckCircle size={20} className="text-green-600" />;
      }

      if (currentState.status === 'error') {
        return <AlertCircle size={20} className="text-destructive" />;
      }

      return null;
    })();

    // Create the validator object
    const validator = {
      validateFn,
      getMergedProps: (field) => getFieldProps(field, id),
      indicator,
      state: currentState
    };

    // Cache the validator
    validatorsCache.current[cacheKey] = validator;

    return validator;
  }, [validatorsState]);

  // Return the factory object
  return {
    createValidator
  };
};

export default useRemoteValidationFactory;